import { Link } from "react-router-dom";
import CanonicalTag from "@/components/CanonicalTag";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <CanonicalTag url="https://www.udaadu.com/privacy-policy" />
      {/* Header/Navigation Section */}
      <header className="bg-gray-900 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors mr-4">
                Home
              </Link>
              <Link to="/about" className="text-gray-300 hover:text-white transition-colors mr-4">
                About
              </Link>
              <Link to="/services" className="text-gray-300 hover:text-white transition-colors">
                Services
              </Link>
            </div>
            <div>
              <Link to="/contact" className="text-gray-300 hover:text-white transition-colors">
                Contact
              </Link>
            </div>
          </nav>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center text-brand hover:text-brand-foreground mb-6 transition-colors"
        >
          ← Back to Home
        </Link>
        
        {/* Privacy Policy Content */}
        <div className="prose prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-8 text-white">Privacy Policy</h1>
          
          <div className="space-y-8 text-gray-300">
            <div>
              <p className="mb-4"><strong>Last updated January 01, 2025</strong></p>
              
              <p className="mb-4">
                This Privacy Notice for UDA ADU (<strong>"we," "us," or "our"</strong>) describes how and why we might access, collect, store, use, and/or share (<strong>"process"</strong>) your personal information when you use our services (<strong>"Services"</strong>), including when you:
              </p>
              
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Visit our website at <a href="https://udaadu.com" className="text-brand hover:underline">https://udaadu.com</a>, or any website of ours that links to this Privacy Notice</li>
                <li>Engage with us in other related ways, including any sales, marketing, or events</li>
              </ul>
              
              <p className="mb-4">
                <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at info@udaadu.com.
              </p>
            </div>

            {/* Section 1 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">1. WHAT INFORMATION DO WE COLLECT?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>We collect personal information that you provide to us.</em></p>
              
              <p className="mb-4">
                We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
              </p>
              
              <p className="mb-4">
                <strong>Personal Information Provided by You.</strong> The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:
              </p>
              
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>names</li>
                <li>phone numbers</li>
                <li>email addresses</li>
                <li>mailing addresses</li>
                <li>usernames</li>
                <li>passwords</li>
                <li>contact preferences</li>
                <li>contact or authentication data</li>
              </ul>
              
              <p className="mb-4">
                <strong>Sensitive Information.</strong> We do not process sensitive information.
              </p>
              
              <p className="mb-4">
                All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.
              </p>
            </div>

            {/* Section 2 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</em></p>
              
              <p className="mb-4"><strong>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</strong></p>
              
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>To facilitate account creation and authentication and otherwise manage user accounts.</strong> We may process your information so you can create and log in to your account, as well as keep your account in working order.</li>
                <li><strong>To deliver and facilitate delivery of services to the user.</strong> We may process your information to provide you with the requested service.</li>
                <li><strong>To respond to user inquiries/offer support to users.</strong> We may process your information to respond to your inquiries and solve any potential issues you might have with the requested service.</li>
                <li><strong>To send administrative information to you.</strong> We may process your information to send you details about our products and services, changes to our terms and policies, and other similar information.</li>
                <li><strong>To request feedback.</strong> We may process your information when necessary to request feedback and to contact you about your use of our Services.</li>
                <li><strong>To send you marketing and promotional communications.</strong> We may process the personal information you send to us for our marketing purposes, if this is in accordance with your marketing preferences. You can opt out of our marketing emails at any time. For more information, see "<a href="#privacyrights" className="text-brand hover:underline">WHAT ARE YOUR PRIVACY RIGHTS?</a>" below.</li>
                <li><strong>To deliver targeted advertising to you.</strong> We may process your information to develop and display personalized content and advertising tailored to your interests, location, and more.</li>
                <li><strong>To post testimonials.</strong> We post testimonials on our Services that may contain personal information.</li>
                <li><strong>To protect our Services.</strong> We may process your information as part of our efforts to keep our Services safe and secure, including fraud monitoring and prevention.</li>
                <li><strong>To evaluate and improve our Services, products, marketing, and your experience.</strong> We may process your information when we believe it is necessary to identify usage trends, determine the effectiveness of our promotional campaigns, and to evaluate and improve our Services, products, marketing, and your experience.</li>
                <li><strong>To determine the effectiveness of our marketing and promotional campaigns.</strong> We may process your information to better understand how to provide marketing and promotional campaigns that are most relevant to you.</li>
                <li><strong>To comply with our legal obligations.</strong> We may process your information to comply with our legal obligations, respond to legal requests, and exercise, establish, or defend our legal rights.</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>We may share information in specific situations described in this section and/or with the following third parties.</em></p>
              
              <p className="mb-4">
                <strong>Vendors, Consultants, and Other Third-Party Service Providers.</strong> We may share your data with third-party vendors, service providers, contractors, or agents ("<strong>third parties</strong>") who perform services for us or on our behalf and require access to such information to do that work. We have contracts in place with our third parties, which are designed to help safeguard your personal information. This means that they cannot do anything with your personal information unless we have instructed them to do it. They will also not share your personal information with any organization apart from us. They also commit to protect the data they hold on our behalf and to retain it for the period we instruct.
              </p>
              
              <p className="mb-4">The third parties we may share personal information with are as follows:</p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-white">Advertising, Direct Marketing, and Lead Generation</h4>
                  <p className="ml-8">HubSpot CRM and HubSpot Lead Management</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">AI Service Providers</h4>
                  <p className="ml-8">Google Cloud AI and OpenAI</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Communicate and Chat with Users</h4>
                  <p className="ml-8">HubSpot Email, Twilio, bland.ai and gmail</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Content Optimization</h4>
                  <p className="ml-8">GitHub social plugins, Google Calendar widget, Google Fonts, Instagram embed, Lovable.dev and Supabase</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Data Backup and Security</h4>
                  <p className="ml-8">Supabase and Lovable.dev</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Functionality and Infrastructure Optimization</h4>
                  <p className="ml-8">Termly.io, GitHub Pages and Supabase</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Retargeting Platforms</h4>
                  <p className="ml-8">Facebook Custom Audience, Facebook Remarketing and Leadpipe.com</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Social Media Sharing and Advertising</h4>
                  <p className="ml-8">Facebook advertising, Facebook social plugins, Instagram advertising, Google+ social plugins, Pinterest advertising, Tumblr social plugins, LinkedIn advertising and YouTube social plugins</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">User Account Registration and Authentication</h4>
                  <p className="ml-8">Supabase</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">User Commenting and Forums</h4>
                  <p className="ml-8">Facebook Comments, Google Tag Manager, Instagram and Google Business Profile</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Web and Mobile Analytics</h4>
                  <p className="ml-8">Facebook Ads conversion tracking, Facebook Analytics, Google Ads, Google Analytics, Google Tag Manager, Vercel.com, leadpipe.com and HubSpot Analytics</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Website Hosting</h4>
                  <p className="ml-8">hostinger.com, Vercel.com and GitHub</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Website Performance Monitoring</h4>
                  <p className="ml-8">Vercel.com</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white">Website Testing</h4>
                  <p className="ml-8">Google Website Optimizer, Vercel and Vitest</p>
                </div>
              </div>
              
              <p className="mt-4 mb-4">We also may need to share your personal information in the following situations:</p>
              
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>We may use cookies and other tracking technologies to collect and store your information.</em></p>
              
              <p className="mb-4">
                We may use cookies and similar tracking technologies (like web beacons and pixels) to access or store information. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Policy: <a href="/cookie-policy" className="text-brand hover:underline">Cookie Policy</a>.
              </p>
            </div>

            {/* Section 5 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">5. DO WE USE GOOGLE MAPS?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>Yes, we use Google Maps for the purpose of providing better service.</em></p>
              
              <p className="mb-4">
                This website uses Google Maps APIs. You may find the Google Maps APIs Terms of Service <a href="https://cloud.google.com/maps-platform/terms/" className="text-brand hover:underline">here</a>. To further understand Google's Privacy Policy, please refer to this <a href="https://policies.google.com/privacy" className="text-brand hover:underline">link</a>.
              </p>
              
              <p className="mb-4">
                By using our Google Maps API implementation, you agree to be bound by Google's Terms of Service.
              </p>
            </div>

            {/* Section 6 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">6. DO WE USE AI PRODUCTS?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>Yes, we use AI products to provide better service.</em></p>
              
              <p className="mb-4">
                We use AI products such as Google Cloud AI and OpenAI to provide better service.
              </p>
            </div>

            {/* Section 7 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice unless otherwise required by law.</em></p>
              
              <p className="mb-4">
                We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements).
              </p>
              
              <p className="mb-4">
                When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
              </p>
            </div>

            {/* Section 8 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">8. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>We aim to protect your personal information through a system of organizational and technical security measures.</em></p>
              
              <p className="mb-4">
                We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.
              </p>
            </div>

            {/* Section 9 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">9. DO WE COLLECT INFORMATION FROM MINORS?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>We do not knowingly solicit data from or market to children under 18 years of age.</em></p>
              
              <p className="mb-4">
                We do not knowingly solicit data from or market to children under 18 years of age. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at info@udaadu.com.
              </p>
            </div>

            {/* Section 10 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">10. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>You may review, change, or terminate your account at any time.</em></p>
              
              <p className="mb-4">
                If you are located in the European Economic Area and we are processing your information based on your consent, you have the right to withdraw your consent at any time. You can withdraw your consent by contacting us at info@udaadu.com.
              </p>
              
              <p className="mb-4">
                Upon your request, we will terminate your account and remove your information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
              </p>
              
              <p className="mb-4"><strong>Cookies and similar technologies:</strong> Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove cookies and to reject cookies. If you choose to remove cookies or reject cookies, this could affect certain features or services of our Services. To learn more, please visit our Cookie Policy: <a href="/cookie-policy" className="text-brand hover:underline">Cookie Policy</a>.
              </p>
            </div>

            {/* Section 11 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">11. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
              <p className="mb-4">
                Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this privacy notice.
              </p>
            </div>

            {/* Section 12 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">12. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
              <p className="mb-4"><strong><em>In Short:</em></strong> <em>Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>
              
              <p className="mb-4">
                We may update this privacy notice from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible. If we make material changes to this privacy notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this privacy notice frequently to be informed of how we are protecting your information.
              </p>
            </div>

            {/* Section 13 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
              <p className="mb-4">
                If you have questions or comments about this notice, you may contact us by email at info@udaadu.com or by post to:
              </p>
              
              <p className="mb-4">
                UDA ADU<br/>
                3031 Tisch Way Suite 902<br/>
                San Jose, CA 95128<br/>
                United States
              </p>
            </div>

            {/* Section 14 */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h2>
              <p className="mb-4">
                Based on the applicable laws of your country, you may have the rights to request access to the personal information we collect from you, change that information, or delete it in some circumstances. To request to review, update, or delete your personal information, please submit a request form by clicking <a href="mailto:info@udaadu.com" className="text-brand hover:underline">here</a>. We will respond to your request within 30 days.
              </p>
            </div>

            {/* Section 15 - Only for California Residents */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">15. NOTICE FOR CALIFORNIA RESIDENTS</h2>
              <p className="mb-4">
                This section applies only to California residents. Under the California Consumer Privacy Act (CCPA), you have rights regarding your personal information.
              </p>
              
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Know:</strong> You have the right to request that we disclose to you what personal information we collect, use, disclose, and sell.</li>
                <li><strong>Right to Delete:</strong> You have the right to request that we delete personal information that we have collected from you.</li>
                <li><strong>Right to Opt-Out:</strong> You have the right to opt-out of the sale of your personal information.</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.</li>
              </ul>
              
              <p className="mb-4">
                To exercise these rights, please contact us at info@udaadu.com. We will respond to your request within 45 days.
              </p>
            </div>

            {/* Section 16 - Only for Virginia Residents */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">16. NOTICE FOR VIRGINIA RESIDENTS</h2>
              <p className="mb-4">
                Under the Virginia Consumer Data Protection Act (CDPA), Virginia residents have specific rights regarding their personal data.
              </p>
              
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Know and Access:</strong> You have the right to confirm whether we process your personal data and to access such personal data.</li>
                <li><strong>Right to Correct:</strong> You have the right to correct inaccuracies in your personal data.</li>
                <li><strong>Right to Delete:</strong> You have the right to request the deletion of your personal data.</li>
                <li><strong>Right to Obtain a Copy:</strong> You have the right to obtain a copy of your personal data in a portable and, to the extent technically feasible, readily usable format.</li>
                <li><strong>Right to Opt-Out:</strong> You have the right to opt out of the processing of your personal data for purposes of targeted advertising, the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects concerning the consumer.</li>
              </ul>
              
              <p className="mb-4">
                To exercise these rights, please contact us at info@udaadu.com. We will respond to your request within 45 days.
              </p>
            </div>

            {/* Section 17 - Only for Colorado Residents */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">17. NOTICE FOR COLORADO RESIDENTS</h2>
              <p className="mb-4">
                Under the Colorado Privacy Act (CPA), Colorado residents have specific rights regarding their personal data.
              </p>
              
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Access:</strong> You have the right to confirm whether we process your personal data and to access such personal data.</li>
                <li><strong>Right to Correct:</strong> You have the right to correct inaccuracies in your personal data.</li>
                <li><strong>Right to Delete:</strong> You have the right to request the deletion of your personal data.</li>
                <li><strong>Right to Obtain a Copy:</strong> You have the right to obtain a copy of your personal data in a portable and, to the extent technically feasible, readily usable format.</li>
                <li><strong>Right to Opt-Out:</strong> You have the right to opt out of the processing of your personal data for purposes of targeted advertising, the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects concerning the consumer.</li>
              </ul>
              
              <p className="mb-4">
                To exercise these rights, please contact us at info@udaadu.com. We will respond to your request within 45 days.
              </p>
            </div>

            {/* Section 18 - Only for Connecticut Residents */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">18. NOTICE FOR CONNECTICUT RESIDENTS</h2>
              <p className="mb-4">
                Under the Connecticut Data Privacy Act (CTDPA), Connecticut residents have specific rights regarding their personal data.
              </p>
              
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Access:</strong> You have the right to confirm whether we process your personal data and to access such personal data.</li>
                <li><strong>Right to Correct:</strong> You have the right to correct inaccuracies in your personal data.</li>
                <li><strong>Right to Delete:</strong> You have the right to request the deletion of your personal data.</li>
                <li><strong>Right to Obtain a Copy:</strong> You have the right to obtain a copy of your personal data in a portable and, to the extent technically feasible, readily usable format.</li>
                <li><strong>Right to Opt-Out:</strong> You have the right to opt out of the processing of your personal data for purposes of targeted advertising, the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects concerning the consumer.</li>
              </ul>
              
              <p className="mb-4">
                To exercise these rights, please contact us at info@udaadu.com. We will respond to your request within 45 days.
              </p>
            </div>

            {/* Section 19 - Only for Utah Residents */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-white">19. NOTICE FOR UTAH RESIDENTS</h2>
              <p className="mb-4">
                Under the Utah Consumer Privacy Act (UCPA), Utah residents have specific rights regarding their personal data.
              </p>
              
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Access:</strong> You have the right to confirm whether we process your personal data and to access such personal data.</li>
                <li><strong>Right to Delete:</strong> You have the right to request the deletion of your personal data.</li>
                <li><strong>Right to Obtain a Copy:</strong> You have the right to obtain a copy of your personal data in a portable and, to the extent technically feasible, readily usable format.</li>
                <li><strong>Right to Opt-Out:</strong> You have the right to opt out of the processing of your personal data for purposes of targeted advertising or the sale of personal data.</li>
              </ul>
              
              <p className="mb-4">
                To exercise these rights, please contact us at info@udaadu.com. We will respond to your request within 45 days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
